/**
 * Phase 3.41 — Impressum unter Unternehmensdaten.
 * Zeigt das aktuell gespeicherte Impressum an, erlaubt direktes Editieren
 * und „Neu aus Stammdaten generieren". Speichert via PATCH /api/users/me/dealer-profile.
 */
import { useState, useEffect } from "react";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { FileText, RefreshCw, Save, Loader2, Lock } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";

export function ImpressumSection({ currentUser, embedded = false }) {
    const { refresh } = useAuth();
    const isFiliale = currentUser.branch_role === "filiale";
    const [text, setText] = useState(currentUser.imprint_text || "");
    const [saving, setSaving] = useState(false);
    const [regen, setRegen] = useState(false);

    // Jun-2026 (User-Wunsch 2a) — Filiale erbt das Impressum des Hauptsitzes
    // fest (read-only). Wir holen den maßgeblichen (vererbten) Text vom
    // Server, damit auch nachträgliche Hauptsitz-Änderungen korrekt erscheinen.
    useEffect(() => {
        if (!isFiliale) return;
        let active = true;
        api.get("/users/me/dealer-profile")
            .then((r) => { if (active) setText(r.data?.imprint_text || ""); })
            .catch(() => {});
        return () => { active = false; };
    }, [isFiliale]);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch("/users/me/dealer-profile", { imprint_text: text });
            await refresh();
            toast.success("Impressum gespeichert");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    const regenerate = async () => {
        setRegen(true);
        try {
            const r = await api.get("/users/me/imprint/preview");
            setText(r.data.imprint_text || "");
            toast.success("Neu aus Stammdaten erzeugt — bitte speichern, um zu übernehmen");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen");
        } finally {
            setRegen(false);
        }
    };

    const filialeBody = (
        <>
            <div className="flex items-start gap-2 rounded-sm border border-gray-200 bg-gray-50 p-3 mb-3" data-testid="imprint-inherited-note">
                <Lock className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                <p className="text-xs text-gray-600">
                    Das Impressum wird zentral vom <strong>Hauptsitz</strong> vorgegeben und kann hier nicht
                    bearbeitet werden. Änderungen nimmt der Hauptsitz vor.
                </p>
            </div>
            <Textarea
                value={text}
                readOnly
                rows={14}
                className="rounded-sm font-mono-num text-xs leading-relaxed bg-gray-50 text-gray-600"
                data-testid="imprint-textarea-readonly"
            />
        </>
    );

    const editableBody = (
        <>
            <p className="text-xs text-gray-600 mb-3">
                Ihr aktuelles Impressum, wie es auf öffentlichen Inseraten und der Händlerseite angezeigt wird.
                Sie können den Text direkt editieren oder aus den aktuellen Stammdaten neu generieren.
            </p>
            <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={14}
                className="rounded-sm font-mono-num text-xs leading-relaxed"
                placeholder={`Noch kein Impressum hinterlegt. Klicken Sie auf „Aus Stammdaten generieren".`}
                data-testid="imprint-textarea"
            />
            <div className="flex flex-wrap gap-2 mt-3">
                <Button
                    onClick={regenerate}
                    disabled={regen || saving}
                    variant="outline"
                    className="rounded-sm font-bold uppercase tracking-wider text-xs"
                    data-testid="imprint-regenerate-btn"
                >
                    {regen ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                    Aus Stammdaten generieren
                </Button>
                <Button
                    onClick={save}
                    disabled={saving || regen}
                    className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider text-xs ml-auto"
                    data-testid="imprint-save-btn"
                >
                    {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                    Impressum speichern
                </Button>
            </div>
        </>
    );

    const body = isFiliale ? filialeBody : editableBody;
    if (embedded) {
        return <div data-testid="imprint-section">{body}</div>;
    }
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="imprint-section">
            <div className="flex items-center gap-2 mb-2">
                <FileText className="w-5 h-5 text-[#FFCC00]" />
                <h3 className="font-display font-bold text-lg">Impressum</h3>
            </div>
            {body}
        </div>
    );
}
